import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function computePremium(sub) {
  if (!sub || !["active", "trialing"].includes(sub.status)) return false;
  if (sub.current_period_end) {
    try {
      if (new Date(sub.current_period_end) < new Date()) return false;
    } catch {}
  }
  return true;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=not auth, obj=auth
  const [profile, setProfile] = useState(undefined); // undefined=loading, null=none, obj=loaded
  const [subscription, setSubscription] = useState(null);
  const [credits, setCredits] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await client.get("/profile");
      setProfile(data.profile ?? null);
      return data.profile ?? null;
    } catch {
      setProfile(null);
      return null;
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const { data } = await client.get("/credits/balance");
      setCredits(data ?? null);
      return data ?? null;
    } catch {
      return null;
    }
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const { data } = await client.get("/subscription");
      setSubscription(data ?? null);
      return data ?? null;
    } catch {
      setSubscription(null);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("melletic_token");
    if (!token) { setUser(false); setProfile(null); setSubscription(null); return; }
    try {
      const [{ data: me }, prof, sub] = await Promise.all([
        client.get("/auth/me"),
        client.get("/profile").then((r) => r.data.profile ?? null).catch(() => null),
        client.get("/subscription").then((r) => r.data ?? null).catch(() => null),
      ]);
      setUser(me);
      setProfile(prof);
      setSubscription(sub);
      client.get("/credits/balance").then((r) => setCredits(r.data ?? null)).catch(() => {});
      return me;
    } catch {
      localStorage.removeItem("melletic_token");
      setUser(false);
      setProfile(null);
      setSubscription(null);
      setCredits(null);
      return false;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const handler = () => { refreshCredits(); };
    window.addEventListener("mel:credits-refresh", handler);
    return () => window.removeEventListener("mel:credits-refresh", handler);
  }, [refreshCredits]);

  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    localStorage.setItem("melletic_token", data.token);
    await Promise.all([loadProfile(), refreshSubscription(), refreshCredits()]);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const lang = localStorage.getItem("melletic_lang") || "en";
    const { data } = await client.post("/auth/register", { email, password, name, origin_url: window.location.origin, lang });
    localStorage.setItem("melletic_token", data.token);
    setProfile(null);
    setSubscription({ status: "free", plan: null });
    setUser(data.user);
    return data;
  };

  const resendVerification = async () => {
    const lang = localStorage.getItem("melletic_lang") || "en";
    const { data } = await client.post("/auth/resend-verification", { origin_url: window.location.origin, lang });
    return data;
  };

  const logout = () => {
    localStorage.removeItem("melletic_token");
    setUser(false);
    setProfile(null);
    setSubscription(null);
    setCredits(null);
  };

  const premium = computePremium(subscription);

  return (
    <AuthContext.Provider value={{ user, profile, subscription, premium, credits, setProfile, setUser, login, register, resendVerification, logout, refresh, loadProfile, refreshSubscription, refreshCredits, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}
